import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  alpha,
  Fade,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import HotelIcon from '@mui/icons-material/Hotel';
import CancelIcon from '@mui/icons-material/Cancel';
import ReservationTable from '../components/Dashboard/ReservationTable';
import DashboardFilters from '../components/Dashboard/DashboardFilters';
import DataChart from '../components/DataChart';
import dashboardService, { DashboardStats, PropertyOccupancy, MarketSegment } from '../services/dashboardService';

const toDateString = (d: Date) => d.toISOString().split('T')[0];

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        border: `1px solid ${alpha(color, 0.15)}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.18)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color, lineHeight: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2,
              bgcolor: alpha(color, 0.1),
              color,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const today = toDateString(new Date());

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [properties, setProperties] = useState<PropertyOccupancy[]>([]);
  const [segments, setSegments] = useState<MarketSegment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  // Applied date range (what was last fetched)
  const [appliedStart, setAppliedStart] = useState<string>(today);
  const [appliedEnd, setAppliedEnd] = useState<string>(today);

  const fetchDashboardData = useCallback(async (start: string, end: string) => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, propertiesData, segmentsData] = await Promise.all([
        dashboardService.getStats(start, end),
        dashboardService.getPropertyOccupancy(start, end),
        dashboardService.getMarketSegmentation(start, end),
      ]);

      setStats(statsData);
      setProperties(propertiesData);
      setSegments(segmentsData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load dashboard data. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData(appliedStart, appliedEnd);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = (explicitStart?: string, explicitEnd?: string) => {
    const start = explicitStart ?? startDate;
    const end = explicitEnd ?? endDate;
    // Keep picker state in sync when called from a preset
    if (explicitStart) setStartDate(explicitStart);
    if (explicitEnd) setEndDate(explicitEnd);
    setAppliedStart(start);
    setAppliedEnd(end);
    fetchDashboardData(start, end);
  };

  const handleRefresh = () => {
    fetchDashboardData(appliedStart, appliedEnd);
  };

  // Chart data
  const propertyOccupancyData = {
    series: [{ name: 'Check-ins', data: properties.length > 0 ? properties.map(p => p.check_ins) : [0] }],
    xaxis: { categories: properties.length > 0 ? properties.map(p => p.name) : ['No Data'] },
  };

  const segmentDistributionData = {
    series: segments.length > 0 ? segments.map(s => s.count) : [1],
    labels: segments.length > 0 ? segments.map(s => s.name) : ['No Data'],
  };

  const formatDateRange = () => {
    if (appliedStart === appliedEnd) {
      return new Date(appliedStart + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
      });
    }
    return `${new Date(appliedStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(appliedEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="body1" color="text.secondary">Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`, pb: 4 }}>

      {/* Header */}
      <Fade in timeout={500}>
        <Box sx={{
          mb: 4, pt: 3, pb: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          borderRadius: '0 0 24px 24px',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`
        }}>
          <Box sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.25 }}>
                  Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.8) }}>
                  {formatDateRange()} · Last updated {lastUpdated.toLocaleTimeString()}
                </Typography>
              </Box>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.3) }, '&:disabled': { bgcolor: alpha('#fff', 0.1) } }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Fade>

      <Box sx={{ px: 3 }}>

        {/* Filters */}
        <Fade in timeout={600}>
          <Box sx={{ mb: 3, mt: -2 }}>
            <DashboardFilters
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onApplyFilters={handleApplyFilters}
            />
          </Box>
        </Fade>

        {error && (
          <Fade in timeout={400}>
            <Card sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 2, border: `1px solid ${theme.palette.error.main}` }}>
              <Typography color="error.dark" sx={{ fontWeight: 500 }}>{error}</Typography>
            </Card>
          </Fade>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Stat Cards */}
        <Fade in timeout={700}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2.5,
            mb: 4
          }}>
            <StatCard
              title="Check-ins"
              value={stats?.check_ins ?? 0}
              icon={<LoginIcon />}
              color={theme.palette.success.main}
              subtitle="in selected period"
            />
            <StatCard
              title="Check-outs"
              value={stats?.check_outs ?? 0}
              icon={<LogoutIcon />}
              color={theme.palette.warning.main}
              subtitle="in selected period"
            />
          </Box>
        </Fade>

        {/* Property-wise breakdown (only when there's activity) */}
        {stats?.property_stats && stats.property_stats.length > 0 && (
          <Fade in timeout={800}>
            <Card sx={{
              mb: 4, borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <HotelIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Property Activity
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                    {formatDateRange()}
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                  gap: 2
                }}>
                  {stats.property_stats.map((prop) => (
                    <Box
                      key={prop.property_id}
                      sx={{
                        p: 2, borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        transition: 'all 0.2s ease',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), transform: 'translateY(-1px)' }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        {prop.property_name}
                      </Typography>
                      <Stack spacing={0.75}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Check-ins</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                            {prop.check_ins}
                          </Typography>
                        </Box>
                        <Divider sx={{ opacity: 0.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Check-outs</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                            {prop.check_outs}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Charts */}
        <Fade in timeout={900}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 4 }}>
            <DataChart
              title="Check-ins by Property"
              subtitle={formatDateRange()}
              type="bar"
              series={propertyOccupancyData.series}
              height={380}
              colors={[theme.palette.success.main]}
              xaxis={propertyOccupancyData.xaxis}
            />
            <DataChart
              title="Guest Origin"
              subtitle={`Domestic vs International · ${formatDateRange()}`}
              type="donut"
              series={segmentDistributionData.series}
              height={380}
              labels={segmentDistributionData.labels}
            />
          </Box>
        </Fade>

        {/* Reservations Table */}
        <Fade in timeout={1000}>
          <Box>
            <ReservationTable startDate={appliedStart} endDate={appliedEnd} />
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default Dashboard;
